import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import axios from 'axios'
import API_URL from '../../config'
import { setAuthToken } from '../../services/auth'

// Time window options
const TIME_WINDOW_OPTIONS = [
  { value: '1h', label: '1 Hour' },
  { value: '24h', label: '24 Hours' },
  { value: '7d', label: '7 Days' },
  { value: '30d', label: '30 Days' },
  { value: 'custom', label: 'Custom Range' },
]

// Status options for filtering
const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'active', label: 'Active/Complete' },
  { value: 'pending', label: 'Pending' },
  { value: 'processing', label: 'Processing' },
  { value: 'error', label: 'Error' },
  { value: 'skipped', label: 'Skipped' },
]

// Media type options for filtering
const MEDIA_TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'song', label: 'Songs' },
  { value: 'album', label: 'Albums' },
  { value: 'playlist', label: 'Playlists' },
  { value: 'mix', label: 'Mixes' },
]

// Service options for filtering
const SERVICE_OPTIONS = [
  { value: '', label: 'All Services' },
  { value: 'spotify', label: 'Spotify' },
  { value: 'apple', label: 'Apple Music' },
  { value: 'bandcamp', label: 'Bandcamp' },
  { value: 'youtube', label: 'YouTube' },
]

const RecentRecordsView = () => {
  // State for time window
  const [timeWindow, setTimeWindow] = useState('24h')

  // State for custom date range
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  // State for filters
  const [filters, setFilters] = useState({
    mediaType: '',
    status: '',
    service: '',
  })

  // State for records
  const [records, setRecords] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Pagination state
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [totalPages, setTotalPages] = useState(1)
  const [totalResults, setTotalResults] = useState(0)

  // Function to fetch data from API
  const fetchData = async () => {
    try {
      setLoading(true)

      // Set auth token for the request
      setAuthToken(localStorage.getItem('token'))

      // Build params
      const params = {
        timeWindow,
        page,
        pageSize,
        filters: JSON.stringify(filters),
      }

      // Add custom date range if selected
      if (timeWindow === 'custom') {
        if (!startDate) {
          setError('Start date is required for custom time range')
          setLoading(false)
          return
        }

        params.startDate = startDate

        if (endDate) {
          params.endDate = endDate
        }
      }

      // Make API request
      const response = await axios.get(`${API_URL}admin/reconciliation/recent/`, { params })

      // Update state with results
      setRecords(response.data.results)
      setTotalPages(response.data.pagination.totalPages)
      setTotalResults(response.data.pagination.totalResults)
      setError(null)
    } catch (err) {
      console.error('Error fetching recent records:', err)
      setError('Failed to load recent records. Please try again later.')
    } finally {
      setLoading(false)
    }
  }

  // Fetch data on initial load and when dependencies change
  useEffect(() => {
    fetchData()
  }, [timeWindow, page, pageSize, filters])

  // Function to handle time window change
  const handleTimeWindowChange = (e) => {
    setTimeWindow(e.target.value)
    setPage(1) // Reset page when changing time window
  }

  // Function to handle filter changes
  const handleFilterChange = (filterName, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [filterName]: value,
    }))
    setPage(1) // Reset page when changing filters
  }

  // Function to reset filters
  const resetFilters = () => {
    setFilters({
      mediaType: '',
      status: '',
      service: '',
    })
    setPage(1)
  }

  // Function to handle pagination
  const goToPage = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage)
    }
  }

  // Function to render status icon
  const renderStatusIcon = (status) => {
    if (status === 'active' || status === 'complete') {
      return <span className='text-green-500'>✓</span>
    } else if (status === 'pending' || status === 'processing') {
      return <span className='text-yellow-500'>⟳</span>
    } else {
      return <span className='text-red-500'>✗</span>
    }
  }

  // Format date for display
  const formatDate = (isoDate) => {
    if (!isoDate) return ''
    const date = new Date(isoDate)
    return date.toLocaleString()
  }

  return (
    <div className='p-4'>
      <h1 className='mb-6 text-2xl font-bold'>Recent Reconciliations</h1>

      {/* Navigation */}
      <div className='mb-6 flex border-b pb-2'>
        <Link to='/admin/reconciliation' className='px-4 py-2'>
          Dashboard
        </Link>
        <Link to='/admin/reconciliation/recent' className='px-4 py-2 font-bold'>
          Recent
        </Link>
        <Link to='/admin/reconciliation/search' className='px-4 py-2'>
          Search
        </Link>
        <Link to='/admin/reconciliation/manual' className='px-4 py-2'>
          Manual
        </Link>
      </div>

      {/* Time Window Selector */}
      <div className='mb-6'>
        <label className='mb-2 block font-semibold'>Time Window:</label>
        <div className='flex flex-wrap gap-4'>
          {TIME_WINDOW_OPTIONS.map((option) => (
            <label key={option.value} className='inline-flex items-center'>
              <input
                type='radio'
                value={option.value}
                checked={timeWindow === option.value}
                onChange={handleTimeWindowChange}
                className='mr-2'
              />
              {option.label}
            </label>
          ))}
        </div>

        {/* Custom date range inputs */}
        {timeWindow === 'custom' && (
          <div className='mt-3 grid grid-cols-2 gap-4'>
            <div>
              <label className='mb-1 block'>Start Date:</label>
              <input
                type='datetime-local'
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className='w-full border p-2'
                required
              />
            </div>
            <div>
              <label className='mb-1 block'>End Date:</label>
              <input
                type='datetime-local'
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className='w-full border p-2'
              />
            </div>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className='mb-6'>
        <div className='flex flex-wrap items-center gap-4'>
          <div>
            <label className='mr-2'>Media Type:</label>
            <select
              value={filters.mediaType}
              onChange={(e) => handleFilterChange('mediaType', e.target.value)}
              className='border p-2'
            >
              {MEDIA_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className='mr-2'>Status:</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className='border p-2'
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className='mr-2'>Service:</label>
            <select
              value={filters.service}
              onChange={(e) => handleFilterChange('service', e.target.value)}
              className='border p-2'
            >
              {SERVICE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={resetFilters}
            className='bg-gray-200 hover:bg-gray-300 rounded px-4 py-2'
          >
            Reset
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && <div className='bg-red-100 text-red-700 mb-6 rounded p-4'>{error}</div>}

      {/* Results Table */}
      <div className='mb-6 overflow-x-auto'>
        {loading ? (
          <div className='p-4 text-center'>Loading records...</div>
        ) : (
          <table className='min-w-full border'>
            <thead>
              <tr className='bg-gray-100'>
                <th className='border px-4 py-2'>Status</th>
                <th className='border px-4 py-2'>Type</th>
                <th className='border px-4 py-2'>Title</th>
                <th className='border px-4 py-2'>Artists</th>
                <th className='border px-4 py-2'>Service</th>
                <th className='border px-4 py-2'>Processed</th>
                <th className='border px-4 py-2'>Actions</th>
              </tr>
            </thead>
            <tbody>
              {records.length > 0 ? (
                records.map((record) => (
                  <tr
                    key={`${record.id}-${record.instanceId}-${record.service}`}
                    className='hover:bg-gray-50'
                  >
                    <td className='border px-4 py-2 text-center'>
                      {renderStatusIcon(record.status)}
                    </td>
                    <td className='border px-4 py-2 capitalize'>{record.mediaType || 'Unknown'}</td>
                    <td className='border px-4 py-2'>{record.title}</td>
                    <td className='border px-4 py-2'>{record.artists.join(', ')}</td>
                    <td className='border px-4 py-2'>{record.service || 'Unknown'}</td>
                    <td className='border px-4 py-2'>{formatDate(record.processingTime)}</td>
                    <td className='border px-4 py-2'>
                      <Link
                        to={`/admin/reconciliation/record/${record.id}`}
                        className='bg-blue-500 text-white hover:bg-blue-600 rounded px-2 py-1'
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan='7' className='border px-4 py-2 text-center'>
                    No records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <div className='flex items-center justify-between'>
        <div>
          Showing {records.length} of {totalResults} results
        </div>
        <div className='flex items-center gap-2'>
          <button
            onClick={() => goToPage(page - 1)}
            disabled={page === 1}
            className={`rounded px-3 py-1 ${page === 1 ? 'bg-gray-200 cursor-not-allowed' : 'bg-gray-200 hover:bg-gray-300'}`}
          >
            &lt; Previous
          </button>
          <span>
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => goToPage(page + 1)}
            disabled={page === totalPages}
            className={`rounded px-3 py-1 ${page === totalPages ? 'bg-gray-200 cursor-not-allowed' : 'bg-gray-200 hover:bg-gray-300'}`}
          >
            Next &gt;
          </button>
        </div>
      </div>
    </div>
  )
}

export default RecentRecordsView
